// 빠른 설문지의 로컬 답안·즉시 저장(디바운스)·진행률 로직(렌더와 분리한 단일 훅).
//
// fieldId 단위 로컬 답안 맵과 저장/저장중 키 집합, 숫자 입력 디바운스 타이머를 그대로
// 보유한다(동작 보존). 렌더 컴포넌트(QuickSurveySheet)는 이 훅이 돌려주는 값만 그린다.

import { useCallback, useMemo, useRef, useState } from 'react'
import { saveAnswers } from '~/api/assessment'
import { ApiError } from '~/api/client'
import type { AnswerInput, ChecklistItem, SurveyAnswer } from '~/api/types'
import { itemRows } from './quick-survey-model'

export interface UseQuickSurveyParams {
    assessmentId: string
    checklist: ChecklistItem[] | null
    answers: SurveyAnswer[]
    /** 저장 성공 후 상위 답안 상태를 갱신하도록 알린다. */
    onSaved(): void
}

export function useQuickSurvey({
    assessmentId,
    checklist,
    answers,
    onSaved,
}: UseQuickSurveyParams) {
    // fieldId → 로컬 답(서버 저장 전에도 즉시 반영).
    const [local, setLocal] = useState<Map<string, SurveyAnswer>>(() => {
        const map = new Map<string, SurveyAnswer>()
        for (const a of answers) map.set(a.fieldId, a)
        return map
    })
    // 마지막으로 저장 완료된 fieldId(✓ 표시용).
    const [savedKeys, setSavedKeys] = useState<Set<string>>(new Set())
    const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set())
    const [error, setError] = useState<string | null>(null)
    // 숫자 입력 디바운스 타이머.
    const debounceRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
        new Map()
    )

    const items = checklist ?? []

    const allRows = useMemo(
        () => items.flatMap((item) => itemRows(item).map((row) => row.key)),
        [items]
    )
    const totalFields = allRows.length
    const answeredFields = allRows.filter((key) => {
        const a = local.get(key)
        return a != null && (a.unknown || (a.value ?? '').trim() !== '')
    }).length
    const percent =
        totalFields > 0 ? Math.round((answeredFields / totalFields) * 100) : 0

    const persist = useCallback(
        async (fieldId: string, input: AnswerInput) => {
            setSavingKeys((prev) => new Set(prev).add(fieldId))
            setError(null)
            try {
                await saveAnswers(assessmentId, { answers: [input] })
                setSavedKeys((prev) => new Set(prev).add(fieldId))
                onSaved()
            } catch (err: unknown) {
                setError(
                    err instanceof ApiError
                        ? err.message
                        : '답변 저장에 실패했어요. 다시 시도해 주세요.'
                )
            } finally {
                setSavingKeys((prev) => {
                    const next = new Set(prev)
                    next.delete(fieldId)
                    return next
                })
            }
        },
        [assessmentId, onSaved]
    )

    const setLocalAnswer = useCallback(
        (fieldId: string, answer: SurveyAnswer) => {
            setLocal((prev) => {
                const next = new Map(prev)
                next.set(fieldId, answer)
                return next
            })
            setSavedKeys((prev) => {
                if (!prev.has(fieldId)) return prev
                const next = new Set(prev)
                next.delete(fieldId)
                return next
            })
        },
        []
    )

    // 여부형/모름 칩 — 즉시 저장.
    const chooseChip = useCallback(
        (fieldId: string, choice: '예' | '아니오' | '모름') => {
            if (choice === '모름') {
                setLocalAnswer(fieldId, { fieldId, value: '', unknown: true })
                void persist(fieldId, { fieldId, unknown: true })
            } else {
                setLocalAnswer(fieldId, {
                    fieldId,
                    value: choice,
                    unknown: false,
                })
                void persist(fieldId, { fieldId, value: choice })
            }
        },
        [persist, setLocalAnswer]
    )

    // 숫자 입력 — 로컬 갱신 + 디바운스 저장.
    const changeNumber = useCallback(
        (fieldId: string, raw: string) => {
            setLocalAnswer(fieldId, { fieldId, value: raw, unknown: false })
            const timers = debounceRef.current
            const existing = timers.get(fieldId)
            if (existing) clearTimeout(existing)
            const handle = setTimeout(() => {
                timers.delete(fieldId)
                const trimmed = raw.trim()
                if (trimmed === '') return
                void persist(fieldId, { fieldId, value: trimmed })
            }, 600)
            timers.set(fieldId, handle)
        },
        [persist, setLocalAnswer]
    )

    return {
        items,
        totalFields,
        answeredFields,
        percent,
        error,
        local,
        savingKeys,
        savedKeys,
        chooseChip,
        changeNumber,
    }
}
